/**
 * Honesty for leftover-dev hosting URLs on tenant=prod MCP / app-picker surfaces.
 *
 * Loopback and docker-bridge hosts are leftover workstation residue — not
 * live cluster endpoints. Label them. Do not rewrite into invented prod URLs.
 * Live Weather / Trekker / Drone / GEO / Markets hosts pass through.
 */

export const LEFTOVER_DEV = 'leftover_dev';
export const LIVE = 'live';
export const ABSENT = 'absent';
export const INACTIVE_TOMBSTONE = 'inactive_tombstone';

export type HostingHonesty = typeof LEFTOVER_DEV | typeof LIVE | typeof ABSENT;
export type LeftoverDevKind = 'loopback' | 'docker_bridge' | 'private';

export type HostingPresentation = {
  hosting_url: string | null;
  hosting_live: boolean;
  hosting_honesty: HostingHonesty;
  hosting_honesty_label: string | null;
  hosting_leftover_url: string | null;
};

export type PickerHonesty = {
  picker_live: boolean;
  picker_honesty: string | null;
  picker_honesty_label: string | null;
};

function hostnameFromUrl(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw).hostname.toLowerCase();
  } catch {
    return '';
  }
}

function ipv4Octets(host: string): number[] | null {
  const m = String(host || '').match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return null;
  const oct = [Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4])];
  if (oct.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null;
  return oct;
}

function isLoopbackHost(host: string): boolean {
  const bare = String(host || '').toLowerCase().split(':')[0].trim();
  return bare === '127.0.0.1'
    || bare === 'localhost'
    || bare === '::1'
    || bare === '[::1]'
    || bare === '0.0.0.0';
}

function isDockerBridgeHost(host: string): boolean {
  const bare = String(host || '').toLowerCase().split(':')[0].trim();
  if (bare === 'host.docker.internal') return true;
  const oct = ipv4Octets(bare);
  if (!oct) return false;
  return oct[0] === 172 && oct[1] >= 16 && oct[1] <= 31;
}

function isOtherPrivateHost(host: string): boolean {
  const bare = String(host || '').toLowerCase().split(':')[0].trim();
  const oct = ipv4Octets(bare);
  if (!oct) return false;
  if (oct[0] === 10) return true;
  if (oct[0] === 192 && oct[1] === 168) return true;
  if (oct[0] === 169 && oct[1] === 254) return true;
  return false;
}

export function leftoverDevKind(url: unknown): LeftoverDevKind | null {
  const host = hostnameFromUrl(url);
  if (!host) return null;
  if (isLoopbackHost(host)) return 'loopback';
  if (isDockerBridgeHost(host)) return 'docker_bridge';
  if (isOtherPrivateHost(host)) return 'private';
  return null;
}

export function isLeftoverDevHost(url: unknown): boolean {
  return leftoverDevKind(url) != null;
}

export function leftoverDevHostLabel(url: unknown): string | null {
  const kind = leftoverDevKind(url);
  if (kind === 'loopback') return 'localhost, not this cluster';
  if (kind === 'docker_bridge') return 'docker-bridge, not this cluster';
  if (kind === 'private') return 'leftover-dev, not this cluster';
  return null;
}

export function isProdTenant(tenantId: unknown): boolean {
  return String(tenantId || '').trim().toLowerCase() === 'prod';
}

export function presentHostingForTenant(input: {
  tenantId?: string | null;
  storedUrl?: string | null;
  fallbackUrl?: string | null;
} = {}): HostingPresentation {
  const stored = String(input.storedUrl || '').trim() || null;
  const fallback = String(input.fallbackUrl || '').trim() || null;
  const candidate = stored || fallback;

  if (!candidate) {
    return {
      hosting_url: null,
      hosting_live: false,
      hosting_honesty: ABSENT,
      hosting_honesty_label: null,
      hosting_leftover_url: null,
    };
  }

  const leftoverUrl = isLeftoverDevHost(stored)
    ? stored
    : (!stored && isLeftoverDevHost(fallback) ? fallback : null);

  if (leftoverUrl && isProdTenant(input.tenantId)) {
    return {
      hosting_url: null,
      hosting_live: false,
      hosting_honesty: LEFTOVER_DEV,
      hosting_honesty_label: leftoverDevHostLabel(leftoverUrl),
      hosting_leftover_url: leftoverUrl,
    };
  }

  return {
    hosting_url: candidate,
    hosting_live: !isLeftoverDevHost(candidate),
    hosting_honesty: isLeftoverDevHost(candidate) ? LEFTOVER_DEV : LIVE,
    hosting_honesty_label: leftoverDevHostLabel(candidate),
    hosting_leftover_url: isLeftoverDevHost(candidate) ? candidate : null,
  };
}

export function presentPickerHonesty(input: {
  tenantId?: string | null;
  status?: string | null;
  hosting?: Pick<HostingPresentation, 'hosting_honesty' | 'hosting_honesty_label'> | null;
} = {}): PickerHonesty {
  const statusNorm = String(input.status || '').trim().toLowerCase();
  if (isProdTenant(input.tenantId) && statusNorm === 'inactive') {
    return {
      picker_live: false,
      picker_honesty: INACTIVE_TOMBSTONE,
      picker_honesty_label: 'inactive tombstone',
    };
  }
  if (isProdTenant(input.tenantId) && input.hosting?.hosting_honesty === LEFTOVER_DEV) {
    return {
      picker_live: false,
      picker_honesty: LEFTOVER_DEV,
      picker_honesty_label: input.hosting.hosting_honesty_label || 'leftover-dev, not this cluster',
    };
  }
  return {
    picker_live: input.hosting?.hosting_honesty !== LEFTOVER_DEV,
    picker_honesty: input.hosting?.hosting_honesty === LEFTOVER_DEV ? LEFTOVER_DEV : null,
    picker_honesty_label: input.hosting?.hosting_honesty === LEFTOVER_DEV
      ? (input.hosting.hosting_honesty_label || 'leftover-dev, not this cluster')
      : null,
  };
}

export type HostingDisplay = {
  liveUrl: string | null;
  leftover: boolean;
  label: string | null;
};

/**
 * MCP Hosting column: leftover-dev is labelled, never a live "Open" link.
 * Client-side fallback classifies a leftover hosting_url if the API has not
 * stamped honesty yet (frontend-only deploys).
 */
export function displayHosting(input: {
  tenantId?: string | null;
  hosting_url?: string | null;
  custom_domain?: string | null;
  hosting_honesty?: string | null;
  hosting_honesty_label?: string | null;
  hosting_leftover_url?: string | null;
}): HostingDisplay {
  const leftoverUrl = String(input.hosting_leftover_url || '').trim() || null;
  const rawUrl = String(input.hosting_url || '').trim() || null;
  const stampedLeftover = input.hosting_honesty === LEFTOVER_DEV;
  const inferredLeftover = isProdTenant(input.tenantId) && isLeftoverDevHost(rawUrl || leftoverUrl);

  if (stampedLeftover || inferredLeftover) {
    const source = leftoverUrl || (isLeftoverDevHost(rawUrl) ? rawUrl : null);
    return {
      liveUrl: null,
      leftover: true,
      label: input.hosting_honesty_label || leftoverDevHostLabel(source) || 'leftover-dev, not this cluster',
    };
  }

  const custom = String(input.custom_domain || '').trim();
  if (custom) {
    return { liveUrl: `https://${custom}`, leftover: false, label: null };
  }
  if (rawUrl) {
    return { liveUrl: rawUrl, leftover: false, label: null };
  }
  return { liveUrl: null, leftover: false, label: null };
}

export function presentAppPickerLabel(input: {
  name?: string | null;
  app_id?: string | null;
  status?: string | null;
  tenantId?: string | null;
  picker_honesty?: string | null;
  picker_honesty_label?: string | null;
  hosting_url?: string | null;
  hosting_leftover_url?: string | null;
  hosting_honesty?: string | null;
  hosting_honesty_label?: string | null;
}): string {
  const name = String(input.name || '').trim() || input.app_id || 'app';
  const id = String(input.app_id || '').trim();
  const base = id ? `${name} (${id})` : name;

  if (input.picker_honesty_label) {
    return `${base} — ${input.picker_honesty_label}`;
  }

  const hosting = presentHostingForTenant({
    tenantId: input.tenantId,
    storedUrl: input.hosting_leftover_url || input.hosting_url,
  });
  const picker = presentPickerHonesty({
    tenantId: input.tenantId,
    status: input.status,
    hosting: input.hosting_honesty
      ? {
          hosting_honesty: input.hosting_honesty as HostingHonesty,
          hosting_honesty_label: input.hosting_honesty_label || null,
        }
      : hosting,
  });
  if (picker.picker_honesty_label) {
    return `${base} — ${picker.picker_honesty_label}`;
  }
  return base;
}
